/**
 * "Add cohort value" modal (AC-ROSTER-002, AC-ROSTER-006).
 *
 * Team Admin extends the tenant's cohort catalog WITHOUT leaving the player
 * form: the modal posts `POST /cohort-values/v1`, hands the created row back to
 * the caller, and the new value is selected in the dropdown immediately — no
 * navigation, no page reload.
 *
 * `code` is what a player row stores and is immutable afterwards, so the form
 * says so and validates the shape the service enforces
 * (`services/roster/src/constants.ts` → `COHORT_CODE_PATTERN`).
 */
import { useState, type FormEvent } from 'react';

import { useApiClient } from '../../app/providers';
import { OverlayDialog } from '../../components/overlay-dialog';
import { TextField } from '../../components/text-field';
import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import { COHORT_CATEGORIES, type CohortCategory, type CohortValue } from '../../lib/api-types';

/** Mirrors the service's validation so the user is told before the round-trip. */
const COHORT_CODE_PATTERN = /^[A-Za-z0-9-]+$/;
const COHORT_CODE_MAX_LENGTH = 32;
const COHORT_LABEL_MAX_LENGTH = 64;
const COHORT_SORT_ORDER_MAX = 100_000;

export const COHORT_CATEGORY_LABELS: Record<CohortCategory, string> = {
  grade: 'Grade (high school)',
  class: 'Class (college)',
  squad: 'Squad',
  custom: 'Custom',
};

interface CohortValueDraft {
  code: string;
  label: string;
  category: CohortCategory;
  sortOrder: string;
}

/** Returns a field→message map; empty means the draft is valid. */
export function validateCohortValueDraft(
  draft: CohortValueDraft,
  existingCodes: readonly string[]
): Record<string, string> {
  const errors: Record<string, string> = {};
  const code = draft.code.trim();
  if (code === '') errors.code = 'Enter a code, for example SR or VARSITY.';
  else if (code.length > COHORT_CODE_MAX_LENGTH) {
    errors.code = `Use at most ${COHORT_CODE_MAX_LENGTH} characters.`;
  } else if (!COHORT_CODE_PATTERN.test(code)) {
    errors.code = 'Use letters, digits and hyphens only (10, SR, RS-FR, VARSITY).';
  } else if (existingCodes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
    errors.code = 'That code is already in your cohort catalog.';
  }

  const label = draft.label.trim();
  if (label === '') errors.label = 'Enter the text people will see, for example Senior.';
  else if (label.length > COHORT_LABEL_MAX_LENGTH) {
    errors.label = `Use at most ${COHORT_LABEL_MAX_LENGTH} characters.`;
  }

  if (draft.sortOrder.trim() !== '') {
    const parsed = Number(draft.sortOrder);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > COHORT_SORT_ORDER_MAX) {
      errors.sortOrder = `Enter a whole number between 0 and ${COHORT_SORT_ORDER_MAX}.`;
    }
  }
  return errors;
}

export interface AddCohortValueModalProps {
  /** Codes already in the catalog — used for the inline duplicate check. */
  existingCodes: readonly string[];
  onCancel: () => void;
  /** Called with the persisted row; the caller selects it in the dropdown. */
  onCreated: (value: CohortValue) => void;
}

export function AddCohortValueModal({
  existingCodes,
  onCancel,
  onCreated,
}: AddCohortValueModalProps): JSX.Element {
  const apiClient = useApiClient();
  const [draft, setDraft] = useState<CohortValueDraft>({
    code: '',
    label: '',
    category: 'grade',
    sortOrder: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function update(patch: Partial<CohortValueDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const errors = validateCohortValueDraft(draft, existingCodes);
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      const created = await apiClient.createCohortValue({
        code: draft.code.trim(),
        label: draft.label.trim(),
        category: draft.category,
        ...(draft.sortOrder.trim() === '' ? {} : { sort_order: Number(draft.sortOrder) }),
      });
      onCreated(created);
    } catch (caught) {
      if (ApiError.isApiError(caught)) {
        setFormError(caught.message);
        if (caught.field !== null) setFieldErrors({ [caught.field]: caught.message });
      } else {
        setFormError('The cohort value could not be saved. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <OverlayDialog title="Add Cohort Value" onClose={onCancel}>
      <UnsavedChangesGuard
        isDirty={draft.code !== '' || draft.label !== '' || draft.sortOrder !== ''}
      />
      <form className="stack roster-form__panel" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <p className="muted">
          Cohort is how Gridiron Tracker groups players by seniority — grade, class year or squad.
          The value is added to this team&apos;s catalog and is available to everyone on the team.
        </p>

        <TextField
          label="Code"
          name="code"
          required
          autoComplete="off"
          value={draft.code}
          onChange={(event) => update({ code: event.target.value })}
          error={fieldErrors.code ?? null}
          hint="Stored on the player record and cannot be changed later. Example: RS-FR."
        />

        <TextField
          label="Label"
          name="label"
          required
          autoComplete="off"
          value={draft.label}
          onChange={(event) => update({ label: event.target.value })}
          error={fieldErrors.label ?? null}
          hint="Shown in dropdowns and on the roster. Example: Redshirt Freshman."
        />

        <div className="field">
          <label className="field__label" htmlFor="cohort-value-category">
            Category
            <span aria-hidden="true"> *</span>
            <span className="visually-hidden"> (required)</span>
          </label>
          <select
            id="cohort-value-category"
            className="field__control"
            value={draft.category}
            onChange={(event) => update({ category: event.target.value as CohortCategory })}
          >
            {COHORT_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {COHORT_CATEGORY_LABELS[category]}
              </option>
            ))}
          </select>
        </div>

        <TextField
          label="Sort order (optional)"
          name="sort_order"
          type="number"
          inputMode="numeric"
          min={0}
          value={draft.sortOrder}
          onChange={(event) => update({ sortOrder: event.target.value })}
          error={fieldErrors.sortOrder ?? null}
          hint="Leave empty to add it to the end of the category."
        />

        {formError !== null ? (
          <div className="banner banner--error" role="alert">
            <p>{formError}</p>
          </div>
        ) : null}

        <div className="btn-row confirm-dialog__actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Add Cohort Value'}
          </button>
        </div>
      </form>
    </OverlayDialog>
  );
}
